export const SITE = {
  name: "Helibrain",
  // V9 rebrand — matches the store name "Helibrain: Brain Performance"
  tagline: "Brain Performance",
  url: "https://helibrain.com",
  description:
    "Helibrain's AI coach turns neuroscience on sleep, memory, focus and nutrition into one clear thing to do each day, and adjusts it to what you did and how you feel.",
  contactEmail: "hello@helibrain.com",
  companyName: "Helibrain",
};

export const APP_LINKS = {
  // referrer tag (V9 Phase 2 Step 2.4): installs via the website — including
  // everyone arriving from share cards — become attributable in Play Console.
  googlePlay:
    "https://play.google.com/store/apps/details?id=com.helibrain.app&referrer=utm_source%3Dhelibrain.com%26utm_medium%3Dwebsite",
  appStore: "https://apps.apple.com/us/app/helibrain/id6752911563",
};

// The Helibrain web app (Expo web build), no trailing slash. NEXT_PUBLIC_* is
// baked in at build time: hb_infra's ansible sets it per environment; without
// it, `npm run dev` points at the local Expo web port and any build at prod.
export const WEB_APP_URL = (
  process.env.NEXT_PUBLIC_WEB_APP_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:8082"
    : "https://app.helibrain.com")
).replace(/\/+$/, "");

// Web app routes (test-expo-project services/deepLinking.web.ts): Login is
// /auth/login; RegisterStart (/auth) is the sign-up / sign-in choice page.
export const WEB_APP_LINKS = {
  login: `${WEB_APP_URL}/auth/login`,
  signUp: `${WEB_APP_URL}/auth`,
};

/** The one sentence that says what Helibrain is (Organization JSON-LD, /about). */
export const SITE_ENTITY_DESCRIPTION =
  "Helibrain is a brain-health habits app with free, no-sign-up browser brain tests based on published research.";

// The top menu is in lib/nav.ts. The blog shows by itself once content/blog has a
// post (lib/articleSections.ts); the 2023 posts stay retired (hb_infra's Caddy answers
// their URLs with 410).
export const BLOG_LINK = { label: "Blog", href: "/blog" };

export const FEATURES = [
  {
    title: "A coach that plans your day",
    description:
      "Each morning your coach gives you one clear thing to do, with a smaller version for busy days. Tap Give me more or Lighter today, answer the evening check-in with a tap, and the next day's step adjusts. Ask it anything by text or voice, and once a week you review the plan together.",
    screenshot: "/images/screenshots/coach.png",
    alt: "Helibrain AI coach chat logging a breakfast and suggesting brain-healthy foods, with one-tap reply suggestions",
  },
  {
    title: "Science-backed habits, with a schedule",
    description:
      "Every recommendation is grounded in peer-reviewed studies on sleep, memory, focus and stress. When you add one as a habit, it comes with suggested days, a time of day and a dose where the research gives them. You can change all of it.",
    screenshot: "/images/screenshots/recommendations.png",
    alt: "Helibrain app showing personalized recommendations with quick wins and importance levels",
  },
  {
    title: "Track your cognitive performance",
    description:
      "Five short tests measure working memory, reaction time, self-control, reasoning and creativity. Trend graphs with personal bests compare each session with your own baseline.",
    screenshot: "/images/screenshots/cognitive-trends.png",
    alt: "Helibrain cognitive trends screen with working memory and reasoning graphs over time and personal best stars",
  },
  {
    title: "Push today, or recover?",
    description:
      "Connect Apple Health or Health Connect and Helibrain reads your sleep and heart-rate variability — a morning Recovery verdict sets up the day, sleep and HRV trends run against your own baseline, and well-rested nights earn a Brain Score bonus. Read-only, private, never used for ads.",
    screenshot: "/images/screenshots/health.png",
    alt: "Helibrain app showing the Progress screen with sleep duration and heart-rate variability trend graphs synced from a connected health app",
  },
  {
    title: "Scan meals, score your nutrition",
    description:
      "Log any meal and get an instant Daily Score based on how well your diet supports cognitive performance, plus top brain-food picks and smarter swaps.",
    screenshot: "/images/screenshots/nutrition.png",
    alt: "Helibrain app showing nutrition tracking with daily score and brain food recommendations",
  },
];

export const PERSONALIZATION_AREAS = [
  {
    title: "Intelligence",
    description: "Learning, memory, and fluid reasoning",
    icon: "Brain",
  },
  {
    title: "Focus & Self-Control",
    description: "Attention span and willpower",
    icon: "Target",
  },
  {
    title: "Brain Health",
    description: "Long-term cognitive function",
    icon: "HeartPulse",
  },
  {
    title: "Strategic Thinking",
    description: "Problem-solving and decision-making",
    icon: "Lightbulb",
  },
  {
    title: "Stress Resilience",
    description: "Stress management and mental toughness",
    icon: "Shield",
  },
];

export const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Download the app",
    description: "Free on iOS, Android and the web. Set up in under a minute.",
  },
  {
    step: "2",
    title: "Take a quick quiz",
    description:
      "60-90 seconds to set your goals across five brain areas. Your coach builds your first week from the answers.",
  },
  {
    step: "3",
    title: "Do today's one thing",
    description:
      "One step a day, adjusted to how it went. Each week you review it with your coach and choose what comes next.",
  },
];

// Prices and limits match the guided plan build (2026-10-20, hb_docs
// GUIDED_PLAN_SYNTHESIS §5.10): $6.99/mo and $29.99/yr, both with a 7-day free
// trial, or start now without the trial; $19.99 one-time recommendations
// unlock, sold separately. Free limits: 3 habits (gateway FREE_HABIT_LIMIT),
// 2 meal logs a day (FreeTierUsageBanner), 10 coach messages a day
// (FREE_DAILY_MESSAGE_LIMIT); 20 a day during the trial (Robert 2026-10-21). The paid 100/day coach
// limit is fair use and deliberately not advertised.
// Change them together with the app and the store listings.
export const PRICING_PLANS = [
  {
    name: "Free",
    badge: "",
    price: "$0",
    period: "",
    altPrice: "",
    summary: "Everything you need to get started.",
    features: [
      "Cognitive tests and trend graphs",
      "Track up to 3 habits at a time",
      "2 meal logs a day",
      "10 AI coach messages a day",
      "A starter set of recommendations",
    ],
    note: "",
    highlighted: false,
  },
  {
    name: "Guided plan",
    badge: "7-day free trial",
    price: "$6.99",
    period: "/month",
    altPrice: "or $29.99/year (about $2.50 a month)",
    summary: "Your AI coach leads, one day at a time.",
    features: [
      "One clear thing to do each day, adjusted daily",
      "Give me more or Lighter today, whenever you like",
      "A weekly review that sets up the next week",
      "Nudges from your coach at a time that suits you",
      "Chat with your coach by text or voice",
      "Unlimited habits and meal logs",
    ],
    note: "Free for 7 days on monthly or yearly, with 20 coach messages a day during the trial. Or skip the trial and start today. Renews automatically. Cancel anytime in your app store settings.",
    highlighted: true,
  },
  {
    name: "Self-Guided",
    badge: "",
    price: "$19.99",
    period: " one-time",
    altPrice: "",
    summary: "For working through the science on your own.",
    features: [
      "Unlock every recommendation",
      "Includes every recommendation we add later",
      "Pay once, no subscription",
      "Restore it on any device with your account",
    ],
    note: "Not included in the Guided plan. With both, your coach can use every recommendation too.",
    highlighted: false,
  },
];

// Groups on the /tests hub, in page order (2026-11-03). `colors` are the
// gradient behind the test drawings on the hub card pictures
// (scripts/make_test_og_images.mjs thumb mode) and the group's icon tile.
export const TEST_AREAS = [
  {
    id: "speed",
    label: "Speed & attention",
    blurb: "How fast you react, how long you stay focused, and whether you can stop in time.",
    colors: ["#0A2A4F", "#1667C9", "#1BA6C4"],
  },
  {
    id: "memory",
    label: "Memory",
    blurb: "Numbers, squares, words and places: how much you can hold in mind.",
    colors: ["#1A1B57", "#4338CA", "#8B5CF6"],
  },
  {
    id: "reasoning",
    label: "Reasoning & thinking",
    blurb: "Spot the pattern, turn a shape, do the sums, and ignore what gets in the way.",
    colors: ["#06303A", "#0E7490", "#1FA672"],
  },
  {
    id: "creativity",
    label: "Creativity",
    blurb: "How far apart your ideas can go.",
    colors: ["#3B1A63", "#9D3C9A", "#E7746A"],
  },
  // Last on purpose (Robert, 2026-11-04: the new games go at the bottom of the hub for now).
  {
    id: "decisions",
    label: "Decisions & strategy",
    blurb: "Out-think other players, push your luck, and learn which choices pay off.",
    colors: ["#3A1D06", "#B45309", "#F59E0B"],
  },
  // Below the other areas (traffic games 2026-11-04; final order fixed at merge:
  // speed, memory, reasoning, creativity, decisions, skills, perception, personality).
  {
    id: "skills",
    label: "Speed & skill games",
    blurb: "Typing, clicking and aiming: quick games to beat your own best.",
    colors: ["#431407", "#C2410C", "#F59E0B"],
  },
  // Owner: builder E (traffic games, 2026-11-04).
  {
    id: "perception",
    label: "Senses & perception",
    blurb: "How you see colors and hear pitch and rhythm.",
    colors: ["#5A1E0E", "#C2410C", "#F59E0B"],
  },
  // Owner: builder D (traffic games, 2026-11-04). Last on the hub (fixed order at merge:
  // speed, memory, reasoning, creativity, decisions, skills, perception, personality).
  {
    id: "personality",
    label: "You & your mind",
    blurb: "No right answers: what you are like, and what you see when you imagine something.",
    colors: ["#3A1607", "#B4540B", "#E9A23B"],
  },
] as const;

export type TestArea = (typeof TEST_AREAS)[number]["id"];

export type FreeTestPage = {
  label: string;
  href: string;
  test: import("./freeTests").FreeTestId;
  area: TestArea;
  featured?: boolean;
  /** false: not in the footer test lists or the articles' test links (only the hub and its own page). */
  listed?: boolean;
  measures: string;
  time: string;
  hook: string;
  shareScene: string;
};

// Free brain-test pages (2026-10-31): the /tests hub, footer column, sitemap
// and the "more tests" links on each test page. Caddy's @website matcher
// (hb_infra) must list each path too. `measures` and `time` are the hub cards'
// lines; the times repeat what each test page says (title/lead), so change
// both together. `hook` is the question on the test's share image and
// `shareScene` describes that image's drawing for its alt text (both made by
// scripts/make_test_og_images.mjs; re-run it after changing a label or hook).
// `test` is the cognitive service's test code (lib/freeTests.ts FreeTestId).
// `area` is the hub's group (TEST_AREAS); `featured: true` shows the test as
// the wide card at the top of the hub instead of in its group. The hub card
// picture (public/images/tests/<slug>/thumb.webp) comes from the same script.
export const FREE_TEST_PAGES: FreeTestPage[] = [
  {
    label: "IQ test",
    href: "/iq-test",
    test: "iq",
    area: "reasoning",
    featured: true,
    measures: "Reasoning, memory, speed, spatial and verbal skills in five short sections.",
    time: "About 25 minutes",
    hook: "Five sections, one honest estimate",
    shareScene: "five horizontal bars, one per section, with a shaded range on the first",
  },
  {
    label: "Reaction time test",
    href: "/reaction-time-test",
    test: "reaction_time",
    area: "speed",
    measures: "How fast you react when a signal appears.",
    time: "About 1 minute",
    hook: "How fast are you?",
    shareScene: "a green circle that says Tap! and a 247 ms timer",
  },
  {
    label: "Attention span test",
    href: "/attention-span-test",
    test: "attention_span",
    area: "speed",
    measures: "How well you stay ready for a signal when nothing else happens.",
    time: "3 minutes",
    hook: "How long can you stay focused?",
    shareScene: "a grey circle that says Wait, with 2:14 left on the clock",
  },
  {
    label: "Go/no-go test",
    href: "/go-no-go-test",
    test: "go_no_go",
    area: "speed",
    measures: "Whether you can stop yourself from tapping in time.",
    time: "About 1 minute",
    hook: "Can you stop in time?",
    shareScene: "a blue circle that says Tap next to an orange square that says Stop",
  },
  {
    label: "N-back test",
    href: "/n-back-test",
    test: "n_back",
    area: "memory",
    measures: "How much you can hold in mind while it keeps changing.",
    time: "Length depends on how far you climb",
    hook: "How far back can you remember?",
    shareScene: "a 3 by 3 grid with one lit square, marked 2-back",
  },
  {
    label: "Creativity test",
    href: "/creativity-test",
    test: "creativity",
    area: "creativity",
    measures: "How different from each other the words you think of are.",
    time: "A few minutes, no clock",
    hook: "How far apart can your words go?",
    shareScene: "six unrelated words, like volcano, spoon and jazz, linked by lines",
  },
  {
    label: "Matrix reasoning test",
    href: "/matrix-reasoning-test",
    test: "matrix",
    area: "reasoning",
    measures: "How well you spot the rules in a pattern and apply them.",
    time: "No clock",
    hook: "Can you spot the pattern?",
    shareScene: "a 3 by 3 pattern of shapes with the last piece missing",
  },
  {
    label: "Number memory test",
    href: "/number-memory-test",
    test: "number_memory",
    area: "memory",
    measures: "How long a number you can hold in mind and type back.",
    time: "A few minutes",
    hook: "How many digits can you hold?",
    shareScene: "the number 739 146 and a box where it is typed back",
  },
  {
    label: "Stroop test",
    href: "/stroop-test",
    test: "stroop",
    area: "reasoning",
    measures: "How much a word gets in the way when you name its ink color.",
    time: "About 1 minute",
    hook: "Name the ink, not the word",
    shareScene: "color words printed in a different ink color",
  },
  {
    label: "Visual memory test",
    href: "/visual-memory-test",
    test: "visual_memory",
    area: "memory",
    measures: "How much of a pattern of squares you can remember.",
    time: "Length depends on how far you get",
    hook: "How many squares can you remember?",
    shareScene: "a 4 by 4 grid with six lit squares",
  },
  {
    label: "Sequence memory test",
    href: "/sequence-memory-test",
    test: "sequence_memory",
    area: "memory",
    measures: "How long a sequence of places you can repeat in order.",
    time: "A few minutes",
    hook: "Can you repeat the order?",
    shareScene: "a 3 by 3 grid with three squares numbered 1, 2, 3 in the order they lit up",
  },
  {
    label: "Chimp test",
    href: "/chimp-test",
    test: "chimp",
    area: "memory",
    measures: "How many numbers on a grid you can tap in order from memory.",
    time: "Length depends on how far you get",
    hook: "Can you remember where they were?",
    shareScene: "the numbers 1 to 7 scattered on a grid",
  },
  {
    label: "Verbal memory test",
    href: "/verbal-memory-test",
    test: "verbal_memory",
    area: "memory",
    measures: "How long you can tell new words from ones you have seen.",
    time: "Length depends on how far you get",
    hook: "Seen it, or new?",
    shareScene: "the word lantern with Seen and New buttons",
  },
  {
    label: "Math speed test",
    href: "/mental-math-test",
    test: "mental_math",
    area: "reasoning",
    measures: "How many arithmetic problems you solve in a minute.",
    time: "1 minute",
    hook: "How many sums in a minute?",
    shareScene: "the problem 7 × 8 with the answer 56 typed on a number pad",
  },
  {
    label: "Mental rotation test",
    href: "/mental-rotation-test",
    test: "mental_rotation",
    area: "reasoning",
    measures: "How well you can turn a shape in your mind.",
    time: "About 2 minutes",
    hook: "Same shape, or a mirror image?",
    shareScene: "two block shapes, the second one turned, with Same and Mirror buttons",
  },
  {
    label: "Trail making test",
    href: "/trail-making-test",
    test: "trail_making",
    area: "reasoning",
    measures: "How fast you connect circles in order, and switch between numbers and letters.",
    time: "A few minutes",
    hook: "1-A-2-B… how fast can you switch?",
    shareScene: "circles labelled 1, A, 2, B, 3, C joined by a line",
  },
  {
    label: "Processing speed test",
    href: "/symbol-digit-test",
    test: "symbol_digit",
    area: "reasoning",
    measures: "How fast you match symbols to digits with a key.",
    time: "About 2 minutes",
    hook: "How fast can you match them?",
    shareScene: "a key of symbols over the digits 1 to 5 and one symbol to match",
  },
  // Decision games (2026-11-04): at the bottom of the hub and not in the
  // footer lists for now (Robert: "just for some hype", polish later).
  {
    label: "Guess 2/3 of the average",
    href: "/guess-two-thirds-of-the-average",
    test: "two_thirds",
    area: "decisions",
    listed: false,
    measures: "How many steps ahead you think about what everyone else will guess.",
    time: "1 minute, new round every week",
    hook: "Can you outsmart the crowd?",
    shareScene: "a number line with the steps 50, 33, 22, 15 and 10 and a marker on 22",
  },
  {
    label: "Balloon risk game",
    href: "/balloon-risk-game",
    test: "balloon_risk",
    area: "decisions",
    listed: false,
    measures: "How much risk you take when every pump pays but any pump can burst the balloon.",
    time: "About 5 minutes",
    hook: "Pump again, or cash out?",
    shareScene: "a red balloon above a Pump button and a Collect button",
  },
  {
    label: "Four-deck card game",
    href: "/four-deck-card-game",
    test: "iowa_gambling",
    area: "decisions",
    listed: false,
    measures: "How quickly you learn which choices pay off when the rules are hidden.",
    time: "About 5 minutes",
    hook: "Which decks really pay off?",
    shareScene: "four face-down card decks and one turned card showing +100 and −250",
  },
  // Strategy games B (2026-11-04): in the "decisions" group at the bottom of the hub, not in the footer
  // or the articles' test links (like the decision games above).
  {
    label: "Tower of London test",
    href: "/tower-of-london-test",
    test: "tower_of_london",
    area: "decisions",
    listed: false,
    measures: "How well you plan moves ahead to reach a goal in the fewest steps.",
    time: "About 5 minutes",
    hook: "Can you plan the fewest moves?",
    shareScene: "three pegs of different heights with coloured balls, and the goal pattern above them",
  },
  {
    label: "Sense of direction test",
    href: "/sense-of-direction-test",
    test: "sense_of_direction",
    area: "decisions",
    listed: false,
    measures: "How well you find your way from a map you saw once, and point back to where you started.",
    time: "About 5 minutes",
    hook: "Could you find your way back?",
    shareScene: "a hedge maze at night with a lantern's circle of light, numbered flags and an arrow pointing back to a tent",
  },
  // Speed & skill games (2026-11-04): results are kept apart per input
  // (keyboard / phone keyboard, mouse / touch / spacebar).
  {
    label: "Typing speed test",
    href: "/typing-speed-test",
    test: "typing_speed",
    area: "skills",
    listed: false,
    measures: "How many words a minute you type, and how accurately.",
    time: "30 or 60 seconds",
    hook: "How fast do you type?",
    shareScene: "a line of text being typed, the typed part green with one wrong letter in red, and a words-per-minute counter",
  },
  {
    label: "Click speed test",
    href: "/click-speed-test",
    test: "click_speed",
    area: "skills",
    listed: false,
    measures: "How many times you click, tap or press the spacebar in a few seconds.",
    time: "5 or 10 seconds",
    hook: "How fast can you click?",
    shareScene: "a big round button with click ripples and a clicks-per-second counter",
  },
  {
    label: "Aim trainer",
    href: "/aim-trainer",
    test: "aim_trainer",
    area: "skills",
    listed: false,
    measures: "How fast and how accurately you hit targets with a mouse or a finger.",
    time: "About 30 seconds",
    hook: "How fast is your aim?",
    shareScene: "round targets of three sizes on a play area, with a pointer on one of them",
  },
  {
    label: "Big Five personality test",
    href: "/big-five-personality-test",
    test: "big_five",
    area: "personality",
    listed: false,
    measures: "Your five broad personality traits, from 50 public-domain statements.",
    time: "About 7 minutes",
    hook: "Which five traits make you, you?",
    shareScene: "five bars for openness, conscientiousness, extraversion, agreeableness and emotional stability",
  },
  {
    label: "Aphantasia test",
    href: "/aphantasia-test",
    test: "imagery",
    area: "personality",
    listed: false,
    measures: "How vivid the pictures in your mind's eye are, in your own words.",
    time: "About 2 minutes",
    hook: "Can you see pictures in your mind?",
    shareScene: "an apple drawn five times, from a clear picture to a blank outline",
  },
  {
    label: "Color perception test",
    href: "/color-perception-test",
    test: "color_perception",
    area: "perception",
    listed: false,
    measures: "Where green turns blue for you, and how well you put close colors in order.",
    time: "About 3 minutes",
    hook: "Where does your blue begin?",
    shareScene: "a strip of colors from green to blue with a marker where blue begins",
  },
  {
    label: "Tone deaf test",
    href: "/tone-deaf-test",
    test: "musical_ear",
    area: "perception",
    listed: false,
    measures: "How small a step in pitch you can hear, how well you tap back a rhythm, and whether you can name notes.",
    time: "About 4 minutes",
    hook: "How fine is your ear?",
    shareScene: "two tones drawn as bars, the second a little higher, with Lower and Higher buttons",
  },
  {
    label: "Face memory test",
    href: "/face-memory-test",
    test: "face_memory",
    area: "perception",
    listed: false,
    measures: "Learn unfamiliar faces, then recognize them among lookalikes in two views.",
    time: "About 2 minutes",
    hook: "How well do you remember faces?",
    shareScene: "three computer-generated faces of people who don't exist, with one selected",
  },
  {
    label: "Emotion recognition test",
    href: "/emotion-recognition-test",
    test: "emotion_recognition",
    area: "perception",
    listed: false,
    measures: "Match facial expressions to their intended labels, from subtle to strong.",
    time: "About 2 minutes",
    hook: "Which expression do you see?",
    shareScene: "computer-generated faces of people who don't exist showing different expressions",
  },
  {
    label: "Draw a perfect circle",
    href: "/draw-a-perfect-circle",
    test: "perfect_circle",
    area: "skills",
    listed: false,
    measures: "How round a circle you can draw by hand.",
    time: "A few seconds per try",
    hook: "Can you draw a perfect circle?",
    shareScene: "a hand-drawn circle over a dashed perfect circle",
  },
  {
    label: "Time perception test",
    href: "/time-perception-test",
    test: "time_perception",
    area: "skills",
    listed: false,
    measures: "How well you can feel how long a few seconds last.",
    time: "About 1 minute",
    hook: "Can you feel 10 seconds?",
    shareScene: "a stopwatch with its face covered and a Stop button",
  },
  // Traffic games, builder F (2026-11-04): the daily puzzle (last in reasoning) and reading speed (skills).
  {
    label: "Daily logic puzzle",
    href: "/daily-puzzle",
    test: "daily_puzzle",
    area: "reasoning",
    listed: false,
    measures: "A new number-grid puzzle every day, the same for everyone.",
    time: "About 2 to 5 minutes",
    hook: "Today's puzzle: can you crack it?",
    shareScene: "a 5 by 5 number grid with greater-than signs between some squares",
  },
  {
    label: "Reading speed test",
    href: "/reading-speed-test",
    test: "reading_speed",
    area: "skills",
    listed: false,
    measures: "How fast you read, checked with questions about the text.",
    time: "About 3 minutes",
    hook: "How fast do you really read?",
    shareScene: "a page of a short story with a stopwatch and a question with four answers",
  },
  // Brain-area games, set G (2026-11-04): each at the end of its area, not
  // featured, not in the footer.
  {
    label: "Vocabulary test",
    href: "/vocabulary-test",
    test: "vocabulary",
    area: "memory",
    listed: false,
    measures: "How many English words you know, with a range and player comparisons.",
    time: "About 3 minutes",
    hook: "How many English words do you know?",
    shareScene: "a two-step word grid with selected words and a vocabulary band chart",
  },
  {
    label: "Task switching test",
    href: "/task-switching-test",
    test: "task_switching",
    area: "speed",
    listed: false,
    measures: "How fast and accurately you switch between judging color and shape.",
    time: "About 4 minutes",
    hook: "Color or shape? Switch fast",
    shareScene: "an orange circle under a cue that says SHAPE, with two answer buttons",
  },
  {
    label: "Daily general knowledge quiz",
    href: "/general-knowledge-quiz",
    test: "knowledge_quiz",
    area: "memory",
    listed: false,
    measures: "Ten general knowledge questions, the same for everyone and new every day.",
    time: "About 3 minutes, new every day",
    hook: "Ten questions, new every day",
    shareScene: "a quiz question with four answers and a row of green and red squares",
  },
  {
    label: "Three-word riddles",
    href: "/remote-associates-test",
    test: "remote_associates",
    area: "creativity",
    listed: false,
    measures: "Find the one word that links three others. Five new riddles every day.",
    time: "About 3 minutes, new every day",
    hook: "What links these three words?",
    shareScene: "the words green, light and dog, and a box where the linking word is typed",
  },
  {
    "label": "Social shapes challenge",
    "href": "/social-shapes-challenge",
    "test": "social_shapes",
    "area": "perception",
    "listed": false,
    "measures": "Read the intentions in silent animations of moving shapes.",
    "time": "About 6–8 minutes",
    "hook": "What are these shapes thinking?",
    "shareScene": "colored shapes meeting inside a room"
  },
  {
    "label": "Decision traps",
    "href": "/decision-traps-test",
    "test": "decision_traps",
    "area": "decisions",
    "listed": false,
    "measures": "Notice framing, sunk costs and tempting first answers.",
    "time": "About 5–8 minutes",
    "hook": "Would your first answer hold up?",
    "shareScene": "a fork between two choices and a question mark"
  },
  {
    "label": "Brain-health habits check",
    "href": "/brain-health-habits-check",
    "test": "brain_health",
    "area": "personality",
    "listed": false,
    "measures": "Explore the 14 factors in the 2024 Lancet Commission report.",
    "time": "About 3 minutes",
    "hook": "Which topics would you like to explore?",
    "shareScene": "a set of topic cards with check marks"
  },
  {
    "label": "Stress resilience profile",
    "href": "/stress-resilience-profile",
    "test": "stress_resilience",
    "area": "personality",
    "listed": false,
    "measures": "Describe your usual reactions to pressure with public-domain IPIP items.",
    "time": "About 3 minutes",
    "hook": "How do you experience pressure?",
    "shareScene": "three waves settling into a calm line"
  },
];

// The tests listed in the site footers; every other test is one click away via /tests.
export const FOOTER_TEST_HREFS = [
  "/iq-test",
  "/reaction-time-test",
  "/chimp-test",
  "/number-memory-test",
  "/visual-memory-test",
  "/stroop-test",
] as const;

/** Test pages for the articles' test links (FREE_TEST_PAGES without `listed: false`). The footers use FOOTER_TEST_HREFS. */
export const LISTED_TEST_PAGES = FREE_TEST_PAGES.filter((t) => t.listed !== false);

export const FOOTER_LINKS = {
  // A short pick in the footer (2026-11-04: all 17 made a long, untidy column); the
  // hub link under it reaches every test.
  tests: FOOTER_TEST_HREFS.map((href) => FREE_TEST_PAGES.find((t) => t.href === href)!),
  product: [
    { label: "Features", href: "/#features" },
    { label: "Pricing", href: "/#pricing" },
    { label: "Evidence", href: "/evidence" },
    { label: "Download", href: "/#download" },
    { label: "Web app", href: WEB_APP_LINKS.signUp },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
  ],
};
